import { create } from 'zustand'
import { usePanelActivityStore } from './panel-activity-store'

const SELECTION_TIMEOUT_MS = 30_000

interface PanelSelectionState {
  /** Whether panel selection mode is active */
  active: boolean
  /** Filter — only highlight panels of this type (null = all panels) */
  panelType: string | null
  /** Message to show the user */
  message: string | null
  /** Callback to resolve with the selected panel ID */
  resolve: ((panelId: string) => void) | null
  /** Callback to reject (timeout or cancel) */
  reject: ((reason: string) => void) | null
  /**
   * Whether picking a panel starts the AI-activity indicator on it. True for
   * AI-acting flows (`request_panel_selection` — the AI is about to change the
   * panel, so the "working" shimmer is right); false for pure REFERENCE flows
   * like the chat `@`-mention, where selecting a panel only tags it and must
   * NOT leave a "making changes" overlay behind.
   */
  activityOnSelect: boolean

  startSelection: (
    panelType: string | null,
    message: string,
    resolve: (panelId: string) => void,
    reject: (reason: string) => void,
    opts?: { activityOnSelect?: boolean },
  ) => void
  selectPanel: (panelId: string) => void
  cancelSelection: () => void
}

let timeoutId: ReturnType<typeof setTimeout> | null = null

export const usePanelSelectionStore = create<PanelSelectionState>((set, get) => ({
  active: false,
  panelType: null,
  message: null,
  resolve: null,
  reject: null,
  activityOnSelect: true,

  startSelection: (panelType, message, resolve, reject, opts) => {
    // Clear any existing timeout
    if (timeoutId) clearTimeout(timeoutId)

    set({
      active: true,
      panelType,
      message,
      resolve,
      reject,
      activityOnSelect: opts?.activityOnSelect ?? true,
    })

    // Auto-cancel after timeout
    timeoutId = setTimeout(() => {
      const state = get()
      if (state.active) {
        state.reject?.('Panel selection timed out — user did not select a panel.')
        set({
          active: false,
          panelType: null,
          message: null,
          resolve: null,
          reject: null,
          activityOnSelect: true,
        })
      }
    }, SELECTION_TIMEOUT_MS)
  },

  selectPanel: (panelId) => {
    if (timeoutId) clearTimeout(timeoutId)
    const { resolve, panelType, activityOnSelect } = get()

    // Only fire the AI-activity indicator for AI-acting flows. A reference-only
    // pick (chat @-mention) opts out, so no "making changes" shimmer or active
    // overlay lingers on the panel after selection.
    if (activityOnSelect) {
      usePanelActivityStore.getState().startActivity(panelId, panelType ?? undefined)
    }
    resolve?.(panelId)
    set({
      active: false,
      panelType: null,
      message: null,
      resolve: null,
      reject: null,
      activityOnSelect: true,
    })
  },

  cancelSelection: () => {
    if (timeoutId) clearTimeout(timeoutId)
    const { reject } = get()
    reject?.('User cancelled panel selection.')
    set({
      active: false,
      panelType: null,
      message: null,
      resolve: null,
      reject: null,
      activityOnSelect: true,
    })
  },
}))
